import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useSocials } from "../hooks/useSocials";
import HamburgerMenu from "../Components/HamburgerMenu/HamburgerMenu";
import HamburgerButton from "../Components/HamburgerMenu/HamburgerButton";
import { ModalMessage } from "../Components/ModalMessage/ModalMessage";
import Footer from "../Components/Footer/Footer";
import "./SuportePage.css";

const SuportePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout: authLogout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [showLogoutModal, setShowLogoutModal] = React.useState(false);

  // Hook para buscar redes sociais
  const { fetchSocials, socialState } = useSocials();

  // Função para obter o ícone e nome de cada tipo de rede social
  const getSocialTypeInfo = (type: string) => {
    const socialTypes: {
      [key: string]: { icon: string; name: string; color: string };
    } = {
      email: { icon: "📧", name: "Email", color: "#EA4335" },
      phone: { icon: "📞", name: "Telefone", color: "#34A853" },
      website: { icon: "🌐", name: "Website", color: "#4285F4" },
      instagram: { icon: "📸", name: "Instagram", color: "#E4405F" },
      facebook: { icon: "👤", name: "Facebook", color: "#1877F2" },
      whatsapp: { icon: "📱", name: "WhatsApp", color: "#25D366" },
      x: { icon: "🐦", name: "X (Twitter)", color: "#1DA1F2" },
    };

    return socialTypes[type] || { icon: "🔗", name: "Outro", color: "#666" };
  };

  // Buscar redes sociais ao montar o componente
  useEffect(() => {
    fetchSocials();
  }, [fetchSocials]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    authLogout();
    setShowLogoutModal(false);
  };

  return (
    <div className="suporte-page">
      <header className="page-header">
        <div className="header-content">
          <div className="header-left">
            {isAuthenticated && (
              <HamburgerButton isOpen={isMenuOpen} onClick={toggleMenu} />
            )}
          </div>
          <div className="header-actions">
            <button
              onClick={
                isAuthenticated ? handleLogout : () => navigate("/login")
              }
              className="auth-button"
            >
              {isAuthenticated ? "Sair" : "Entrar"}
            </button>
          </div>
        </div>
      </header>

      {isAuthenticated && (
        <HamburgerMenu isOpen={isMenuOpen} onClose={closeMenu} />
      )}

      <main className="page-main">
        <div className="page-title">
          <h1>Suporte</h1>
          <p>Central de ajuda e suporte</p>
        </div>

        <div className="suporte-content">
          <div className="suporte-intro">
            <h2>🆘 Central de Suporte</h2>
            <p>
              Estamos aqui para ajudar! Abaixo você encontrará todos os canais
              de contato disponíveis para suporte, dúvidas ou sugestões. Escolha
              a forma mais conveniente para você e entre em contato conosco.
            </p>
          </div>

          {socialState.isLoading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <span>Carregando canais de contato...</span>
            </div>
          ) : socialState.error ? (
            <div className="error-message">
              ❌ Erro ao carregar canais de contato: {socialState.error}
            </div>
          ) : socialState.socials.length === 0 ? (
            <div className="no-socials-message">
              <p>📭 Nenhum canal de contato disponível no momento.</p>
              <p>
                Entre em contato através do painel administrativo para mais
                informações.
              </p>
            </div>
          ) : (
            <div className="socials-grid">
              <h3>📞 Canais de Contato</h3>
              <div className="socials-list">
                {socialState.socials.map((social) => {
                  const typeInfo = getSocialTypeInfo(social.type);
                  return (
                    <div
                      key={social.id}
                      className="social-item social-item-clickable"
                      onClick={() => {
                        window.open(
                          social.url,
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }}
                      title={`${typeInfo.name} - Clique para acessar`}
                    >
                      <div
                        className="social-icon"
                        style={{ color: typeInfo.color }}
                      >
                        {typeInfo.icon}
                      </div>
                      <div className="social-info">
                        <h4>{typeInfo.name}</h4>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="suporte-footer">
            <p>
              💡 <strong>Dica:</strong> Para dúvidas urgentes, recomendamos usar
              WhatsApp ou telefone. Para questões técnicas, email pode ser mais
              eficiente.
            </p>
          </div>
        </div>
      </main>

      <Footer />

      {showLogoutModal && (
        <ModalMessage
          title="Tem certeza?"
          description="Deseja realmente sair da sua conta?"
          leftButtonText="Sim"
          leftButtonAction={confirmLogout}
          rightButtonText="Não"
          rightButtonAction={() => setShowLogoutModal(false)}
        />
      )}
    </div>
  );
};

export default SuportePage;
